import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { officePresetsGet } from "../../../services/api.service.js";
import { TeamOfficeDesignSelector } from "../tabs/theme/components/team-office-design-selector.js";
import type { ThemeDraft } from "./team-create-flow.js";

export interface TeamCreateThemeSubmitValue {
  presetId: string;
  uploadToken: string | null;
}

interface TeamCreateThemeTabProps {
  themeDraft: ThemeDraft;
  setThemeDraft: (draft: Partial<ThemeDraft>) => void;
  onSubmit: (value: TeamCreateThemeSubmitValue) => Promise<void>;
  submitting: boolean;
}

export const TeamCreateThemeTab = ({
  themeDraft,
  setThemeDraft,
  onSubmit,
  submitting,
}: TeamCreateThemeTabProps) => {
  const presetsQuery = useQuery({
    queryFn: () => officePresetsGet(),
    queryKey: ["officePresetsGet"],
  });

  const officePresets = presetsQuery.data?.presets ?? [];
  const { selectedPresetId } = themeDraft;
  const firstPresetId = officePresets[0]?.id ?? null;

  useEffect(() => {
    if (firstPresetId !== null && selectedPresetId === null) {
      setThemeDraft({ selectedPresetId: firstPresetId });
    }
  }, [firstPresetId, selectedPresetId, setThemeDraft]);

  const selectedPreset = officePresets.find(
    (preset) => preset.id === selectedPresetId
  );

  const selectedPresetDisplayName = selectedPreset?.name ?? "Select a theme";

  const canSubmit = selectedPresetId !== null;

  const handleSubmit = async () => {
    if (!selectedPreset) {
      return;
    }

    await onSubmit({
      presetId: selectedPreset.id,
      uploadToken: null,
    });
  };

  const handlePresetSelect120 = useStableCallback((presetId) =>
    setThemeDraft({ selectedPresetId: presetId })
  );

  return (
    <div className="flex w-full flex-col gap-6">
      <TeamOfficeDesignSelector
        action={{
          disabled: !canSubmit || submitting,
          label: submitting ? "Creating office..." : "Create office",
          onClick: handleSubmit,
        }}
        isDropdownDisabled={presetsQuery.isLoading || submitting}
        onPresetSelect={handlePresetSelect120}
        presets={officePresets}
        selectedPreset={selectedPreset}
        selectedPresetDisplayName={selectedPresetDisplayName}
        selectedPresetId={selectedPresetId}
      />
    </div>
  );
};
